/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { SET_QUOTE_TEMPLATE_EXPIRATION_DATE_MUTATION } from './graphql/setQuoteTemplateExpirationDate';
import { transformQuoteTemplate } from '@/quote-management/data/transforms';
import { NegotiableQuoteTemplateModel } from '@/quote-management/data/models/negotiable-quote-template-model';
import { state } from '@/quote-management/lib/state';
import { events } from '@adobe-commerce/event-bus';

export interface SetQuoteTemplateExpirationDateParams {
  templateId: string;
  expirationDate: string;
}

export const setQuoteTemplateExpirationDate = async (
  params: SetQuoteTemplateExpirationDateParams
): Promise<NegotiableQuoteTemplateModel | null> => {
  if (!state.authenticated) {
    throw new Error('Unauthorized');
  }

  if (!params.templateId) {
    throw new Error('Template ID is required');
  }

  if (!params.expirationDate) {
    throw new Error('Expiration date is required');
  }

  try {
    const results: any = await fetchGraphQl(
      SET_QUOTE_TEMPLATE_EXPIRATION_DATE_MUTATION,
      {
        variables: {
          templateId: params.templateId,
          expirationDate: params.expirationDate,
        },
      }
    );

    if (!results?.data?.setQuoteTemplateExpirationDate) {
      throw new Error('Failed to set expiration date');
    }

    const transformedData = transformQuoteTemplate(
      results.data.setQuoteTemplateExpirationDate
    );

    if (!transformedData) {
      throw new Error('Failed to transform quote template data');
    }

    // Emit event to update the UI
    events.emit('quote-management/quote-template-data', {
      quoteTemplate: transformedData,
      permissions: state.permissions,
    });

    return transformedData;
  } catch (error) {
    return Promise.reject(error);
  }
};

